import { z } from 'zod';
import { didSchema, hexSha256Schema, microAmountSchema, type MicroAmount } from '../validation/primitives.js';

export const LEDGER_DOMAINS = ['CREDIT', 'POSITION'] as const;
export type LedgerDomain = (typeof LEDGER_DOMAINS)[number];

export const ACCOUNT_CLASSIFICATIONS = [
  'PLATFORM_TREASURY',
  'USER_PREPAID_CASH',
  'USER_STIPEND_CASH',
  'ESCROW_CASH_CLEARING',
  'PARTNER_CASH_REVENUE',
  'ISSUER_PRIMARY_POSITION',
  'INVESTOR_CUSTODIED_POSITION',
  'ESCROW_POSITION_CLEARING',
] as const;
export type AccountClassification = (typeof ACCOUNT_CLASSIFICATIONS)[number];

export const DVP_ACTION_TYPES = [
  'DVP_CASH_ESCROW_HOLD',
  'DVP_POSITION_ESCROW_HOLD',
  'DVP_CASH_ESCROW_RELEASE',
  'DVP_POSITION_ESCROW_RELEASE',
] as const;
export type DvpActionType = (typeof DVP_ACTION_TYPES)[number];

/** 14-field wire representation for cryptographic serialization */
export interface JournalRecordWire {
  seq: string;
  txId: string;
  domain: LedgerDomain;
  fromAccountId: string;
  toAccountId: string;
  amountNumeric: string;
  assetIdentifier: string;
  actionType: DvpActionType;
  idempotencyKey: string;
  requestHash: string;
  scopeEnvelopeVersion: number;
  policyDecisionId: string;
  prevRecordHash: string;
  createdAt: string;
}

/** In-memory typed model with branded MicroAmount */
export interface JournalRecord extends Omit<JournalRecordWire, 'amountNumeric' | 'seq'> {
  seq: bigint;
  amount: MicroAmount;
  recordHash: string;
}

export const masterAccountSchema = z.object({
  accountId: z.string().uuid(),
  orgId: z.string().min(1),
  holderDid: didSchema,
  domain: z.enum(LEDGER_DOMAINS),
  classification: z.enum(ACCOUNT_CLASSIFICATIONS),
  assetIdentifier: z.string().min(1),
  balance: microAmountSchema,
  overdraftLimit: microAmountSchema,
});

export type MasterAccount = z.infer<typeof masterAccountSchema>;
